import { AccessManagement } from "@pages/AccountManagement/components/AccessManagement";
import { ErrorBoundary } from "react-error-boundary";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useSearchParams,
} from "react-router-dom";
import { AuthProvider } from "src/contexts/auth";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import {
  AccountManagement,
  AccountManagementIndex,
  ActivationEngagementAccounts,
  FilesAccounts,
  PartnersAccounts,
  ProfilesAccounts,
  PromoCodesAccounts,
  RecruitmentAccounts,
  RecruitersAccounts,
  VerificationQueueAccounts,
} from "@pages/AccountManagement";
import { recruiterAccountsEnabled } from "@data/accountManagementNavLinks";
import { Analyses as AdminAnalyses } from "@pages/AdminDashboard/Analyses";
import { Consultations as AdminConsultations } from "@pages/AdminDashboard/Consultations";
import { Earnings } from "@pages/AdminDashboard/Earnings";
import { Overview as AdminOverview } from "@pages/AdminDashboard/Overview";
import { Prescriptions as AdminPrescriptions } from "@pages/AdminDashboard/Prescriptions";
import { ProfileActivity } from "@pages/AdminDashboard/ProfileActivity";
import { Staff as AdminStaff } from "@pages/AdminDashboard/Staff";
import { BookedAppointments } from "@pages/Appointments/Booked";
import { FollowUp } from "@pages/Appointments/FollowUp";
import { IncomingAppointments } from "@pages/Appointments/Incoming";
import { Masterclass } from "@pages/Appointments/Masterclass";
import { UnifiedSchedule } from "@pages/Appointments/Schedule";
import { SlotTemplates } from "@pages/Appointments/SlotTemplates";
import { VirtualSlots } from "@pages/Appointments/VirtualSlots";
import { ErrorPage } from "@pages/ErrorPage";
import { ForgotPassword } from "@pages/ForgotPassword";
import { OnboardingWizard } from "@pages/Onboarding";
import { Billing } from "@pages/Patients/Billing";
import { Consultation } from "@pages/Patients/Consultation";
import { CreatePatient } from "@pages/Patients/CreatePatient";
import { Documentation } from "@pages/Patients/Documentation";
import { GeneralConsultation } from "@pages/Patients/GeneralConsultation";
import { Immunization } from "@pages/Patients/Immunization";
import { Laboratory } from "@pages/Patients/Laboratory";
import { Medication } from "@pages/Patients/Medication";
import { PatientList } from "@pages/Patients/PatientList";
import { Prescription } from "@pages/Patients/Prescription";
import { Profile } from "@pages/Patients/Profile";
import { Radiology } from "@pages/Patients/Radiology";
import { Timeline } from "@pages/Patients/Timeline";
import { PrivacyPolicy } from "@pages/PrivacyPolicy";
import { BookedAppointments as QueueBookedAppointments } from "@pages/Queue/Booked";
import { IncomingAppointments as QueueIncomingAppointments } from "@pages/Queue/Incoming";
import { Register } from "@pages/Register";
import { ResetPassword } from "@pages/ResetPassword";
import { ClinicUploads } from "@pages/Settings/ClinicUploads";
import { InviteUser } from "@pages/Settings/InviteUser";
import { ResourceManagement } from "@pages/Settings/ResourceManagement";
import { SignIn } from "@pages/SignIn";
import { Analyses as StaffAnalyses } from "@pages/StaffDashboard/Analyses";
import { Consultations as StaffConsultations } from "@pages/StaffDashboard/Consultations";
import { StaffEarningsPage } from "@pages/StaffDashboard/Earnings";
import { Overview as StaffOverview } from "@pages/StaffDashboard/Overview";
import { Prescriptions as StaffPrescriptions } from "@pages/StaffDashboard/Prescriptions";

import { AuthGuard } from "@layouts/AuthGuard";

import { Fallback } from "@components/ErrorFallback";
import { Footer } from "@components/Footer";

import { accountManagementInsightsEnabled } from "@data/accountManagementNavLinks";

import { ROLES } from "@constants/index";


const Root = () => {
  return (
    <ErrorBoundary FallbackComponent={Fallback}>
      <AuthProvider>
        <QueryParamProvider adapter={ReactRouter6Adapter}>
          <Outlet />
          <Footer />
        </QueryParamProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      element={<Root />}
      errorElement={
        <Fallback
          error={undefined}
          resetErrorBoundary={window.location.reload}
        />
      }>
      <Route index element={<SignIn />} />
      <Route path="register" element={<Register />} />
      <Route path="onboard" element={<OnboardingWizard />} />
      <Route path="onboarding" element={<OnboardingWizard />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="privacypolicy" element={<PrivacyPolicy />} />

      <Route path="dashboard/admin">
        <Route index element={<Navigate to="overview" />} />
        <Route
          path="overview"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN]}>
              <AdminOverview />
            </AuthGuard>
          }
        />
        <Route
          path="labs"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN]}>
              <AdminAnalyses />
            </AuthGuard>
          }
        />
        <Route
          path="prescriptions"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN]}>
              <AdminPrescriptions />
            </AuthGuard>
          }
        />
        <Route
          path="staff"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN]}>
              <AdminStaff />
            </AuthGuard>
          }
        />
        <Route
          path="consultations"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN]}>
              <AdminConsultations />
            </AuthGuard>
          }
        />
        <Route
          path="earnings"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN]}>
              <Earnings />
            </AuthGuard>
          }
        />
        <Route
          path="profile-activity"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN]}>
              <ProfileActivity />
            </AuthGuard>
          }
        />
      </Route>

      <Route path="dashboard/staff">
        <Route index element={<Navigate to="overview" />} />
        <Route
          path="overview"
          element={
            <AuthGuard allowedRoles={[ROLES.DOCTOR, ROLES.NURSE]}>
              <StaffOverview />
            </AuthGuard>
          }
        />
        <Route
          path="labs"
          element={
            <AuthGuard allowedRoles={[ROLES.DOCTOR, ROLES.NURSE]}>
              <StaffAnalyses />
            </AuthGuard>
          }
        />
        <Route
          path="prescriptions"
          element={
            <AuthGuard allowedRoles={[ROLES.DOCTOR]}>
              <StaffPrescriptions />
            </AuthGuard>
          }
        />
        <Route
          path="consultations"
          element={
            <AuthGuard allowedRoles={[ROLES.DOCTOR, ROLES.NURSE]}>
              <StaffConsultations />
            </AuthGuard>
          }
        />
        <Route
          path="earnings"
          element={
            <AuthGuard allowedRoles={[ROLES.DOCTOR]}>
              <StaffEarningsPage />
            </AuthGuard>
          }
        />
        <Route
          path="profile-activity"
          element={
            <AuthGuard allowedRoles={[ROLES.DOCTOR]}>
              <ProfileActivity userRole="staff" />
            </AuthGuard>
          }
        />
      </Route>

      {/* <Route path="queue" element={<Patients />} /> */}

      <Route path="patients">
        <Route
          index
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <PatientList />
            </AuthGuard>
          }
        />
        <Route
          path="create"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <CreatePatient />
            </AuthGuard>
          }
        />
        <Route
          path=":patientId/profile"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <Profile />
            </AuthGuard>
          }
        />
        <Route
          path=":patientId/timeline"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <Timeline />
            </AuthGuard>
          }
        />
        <Route path=":patientId/consultation">
          <Route
            index
            element={
              <AuthGuard
                allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
                <Consultation />
              </AuthGuard>
            }
          />
          <Route
            path="general"
            element={
              <AuthGuard
                allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
                <GeneralConsultation />
              </AuthGuard>
            }
          />
          <Route
            path="general/:consultationId"
            element={
              <AuthGuard
                allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
                <GeneralConsultation />
              </AuthGuard>
            }
          />
        </Route>
        <Route
          path=":patientId/laboratory"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <Laboratory />
            </AuthGuard>
          }
        />
        <Route
          path=":patientId/radiology"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <Radiology />
            </AuthGuard>
          }
        />
        <Route path=":patientId/prescription">
          <Route
            index
            element={
              <AuthGuard
                allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
                <Prescription />
              </AuthGuard>
            }
          />
          <Route
            path=":prescriptionId/medication"
            element={
              <AuthGuard
                allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
                <Medication />
              </AuthGuard>
            }
          />
        </Route>
        <Route
          path=":patientId/immunization"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <Immunization />
            </AuthGuard>
          }
        />
        {/* <Route path=":patientId/notes">
          <Route
            index
            element={
              <AuthGuard>
                <Notes />
              </AuthGuard>
            }
          />
          <Route
            path="referral"
            element={
              <AuthGuard>
                <ReferralNote />
              </AuthGuard>
            }
          />
          <Route
            path="sick-note"
            element={
              <AuthGuard>
                <SickNote />
              </AuthGuard>
            }
          />
        </Route> */}
        <Route
          path=":patientId/records"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <Documentation />
            </AuthGuard>
          }
        />
        <Route
          path=":patientId/billing"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <Billing />
            </AuthGuard>
          }
        />
        <Route
          path=":patientId/billing/:consultationId"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE]}>
              <Billing />
            </AuthGuard>
          }
        />
        <Route path="*" element={<ErrorPage />} />
      </Route>

      <Route path="appointments">
        {/* Redirect to booked page because all roles that can see appointments can see "booked". Not true for "incoming" though */}
        <Route index element={<Navigate to="booked" replace />} />
        <Route
          path="schedule"
          element={
            <AuthGuard
              allowedRoles={[
                ROLES.ADMIN,
                ROLES.RECEPTIONIST,
                ROLES.NURSE,
              ]}>
              <UnifiedSchedule />
            </AuthGuard>
          }
        />
        <Route
          path="incoming"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.RECEPTIONIST]}>
              <IncomingAppointments />
            </AuthGuard>
          }
        />
        <Route
          path="booked"
          element={
            <AuthGuard
              allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.RECEPTIONIST]}>
              <BookedAppointments />
            </AuthGuard>
          }
        />
        <Route
          path="slots"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.RECEPTIONIST, ROLES.DOCTOR]}>
              <VirtualSlots />
            </AuthGuard>
          }
        />
        <Route
          path="slot-templates"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR]}>
              <SlotTemplates />
            </AuthGuard>
          }
        />
        <Route
          path="follow-up"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.RECEPTIONIST]} blockedRoles={[ROLES.DOCTOR]} enforceAllowedRoles>
              <FollowUp />
            </AuthGuard>
          }
        />
        <Route
          path="masterclass"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.RECEPTIONIST]}>
              <Masterclass />
            </AuthGuard>
          }
        />
        <Route path="*" element={<ErrorPage />} />
      </Route>

      <Route path="queue">
        {/* Redirect to booked page because all roles that can see appointments can see "booked". Not true for "incoming" though */}
        <Route index element={<Navigate to="booked" replace />} />
        <Route
          path="incoming"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.RECEPTIONIST]}>
              <QueueIncomingAppointments />
            </AuthGuard>
          }
        />
        <Route
          path="booked"
          element={
            <AuthGuard
              allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.RECEPTIONIST]}>
              <QueueBookedAppointments />
            </AuthGuard>
          }
        />
        <Route path="*" element={<ErrorPage />} />
      </Route>

      <Route
        path="account-management"
        element={
          <AuthGuard allowedRoles={[ROLES.ADMIN]}>
            <AccountManagement />
          </AuthGuard>
        }>
        <Route index element={<AccountManagementIndex />} />
        {accountManagementInsightsEnabled && (
          <Route path="recruitment" element={<RecruitmentAccounts />} />
        )}
        {recruiterAccountsEnabled && <Route path="recruiters" element={<RecruitersAccounts />} />}
        <Route
          path="verification-queue"
          element={<VerificationQueueAccounts />}
        />
        <Route path="access" element={<AccessManagement />} />
        <Route path="users" element={<InviteUser />} />
        <Route path="profiles" element={<ProfilesAccounts />} />
        <Route path="partners" element={<PartnersAccounts />} />
        {accountManagementInsightsEnabled && (
          <Route
            path="activation-engagement"
            element={<ActivationEngagementAccounts />}
          />
        )}
        <Route path="files" element={<FilesAccounts />} />
        <Route path="promo-codes" element={<PromoCodesAccounts />} />
        <Route path="*" element={<ErrorPage />} />
      </Route>

      <Route path="settings/admin">
        <Route index element={<Navigate to="resource-management" replace />} />
        <Route
          path="profile"
          element={<Navigate to="/account-management/users" replace />}
        />
        <Route
          path="users"
          element={<Navigate to="/account-management/users" replace />}
        />
        <Route
          path="resource-management"
          element={
            <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE, ROLES.RECEPTIONIST]} enforceAllowedRoles>
              <ResourceManagement />
            </AuthGuard>
          }
        />
        <Route path="*" element={<ErrorPage />} />
      </Route>

      {/* <Route path="settings/staff">
        <Route index element={<Navigate to="profile" />} />
        <Route
          path="profile"
          element={
            <AuthGuard>
              <>Staff profile page</>
            </AuthGuard>
          }
        />
        <Route path="*" element={<ErrorPage />} />
      </Route> */}

      <Route path="*" element={<ErrorPage />} />
    </Route>
  )
);
